// src/context/CurrencyContext.js
//
// HOW CURRENCY WORKS (one simple rule)
// ---------------------------------------------------------------
//  BASE currency    = the currency of the SHOP'S COUNTRY
//                     (Pakistan -> PKR, United Kingdom -> GBP, ...).
//                     It is fixed. Prices are stored / entered in it.
//  DISPLAY currency = what the admin picked on the Currency Settings page.
//                     Only currencies of countries that really have a shop
//                     can be picked. By default it is the base currency.
//
//  Every price in the app is stored in the BASE currency and shown in the
//  DISPLAY currency using live exchange rates:
//        shown = stored x rate(base -> display)
//  If the rate is not available the price is shown in the base currency
//  (never a wrong number).
// ---------------------------------------------------------------
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  fetchLatestRates,
  refreshRates as refreshRatesService,
  getCurrencyCodeFromCountry,
  getCurrencySymbol,
  getCurrencyName,
  describeCurrency,
  rateFromTable,
} from '../utils/exchangeRateService';

const CurrencyContext = createContext();

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

const DEFAULT_BASE = { code: 'PKR', symbol: 'Rs.', name: 'Pakistani Rupee' };

const AVAILABLE_CACHE_KEY = 'available_currencies_cache_v2';
const AVAILABLE_CACHE_TIME_KEY = 'available_currencies_cache_time_v2';
const AVAILABLE_CACHE_MS = 5 * 60 * 1000;

const settingsCacheKey = (shopId) => `currency_settings_v2_${shopId}`;
const settingsCacheTimeKey = (shopId) => `currency_settings_time_v2_${shopId}`;
const SETTINGS_CACHE_MS = 60 * 1000;

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// GET/POST helper. If the saved token is expired the public endpoints still work.
const apiFetch = async (path, options = {}, retryWithoutToken = false) => {
  const token = localStorage.getItem('access_token');
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;

  let response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });

  if (response.status === 401 && token && retryWithoutToken) {
    delete headers.Authorization;
    response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
  }
  return response;
};

const normaliseCurrencyList = (list) => {
  const seen = new Set();
  const out = [];
  (list || []).forEach((item) => {
    const code = String(item?.code || '').toUpperCase();
    if (!code || seen.has(code)) return;
    seen.add(code);
    out.push({
      code,
      symbol: item.symbol || getCurrencySymbol(code),
      name: item.name || getCurrencyName(code),
      countries: Array.isArray(item.countries) ? item.countries : [],
    });
  });
  return out.sort((a, b) => a.name.localeCompare(b.name));
};

export const CurrencyProvider = ({ children }) => {
  const location = useLocation();

  const [shopBase, setShopBase] = useState(DEFAULT_BASE);
  const [display, setDisplay] = useState(DEFAULT_BASE);
  const [rates, setRates] = useState({ PKR: 1 });
  const [ratesFetchedAt, setRatesFetchedAt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [shopId, setShopId] = useState(null);
  const [availableList, setAvailableList] = useState([]);
  const [shopChangeKey, setShopChangeKey] = useState(0);

  const requestIdRef = useRef(0);
  const ratesBaseRef = useRef(null);

  // ---------------------------------------------------------------
  // Which shop is open?
  // ---------------------------------------------------------------
  const getShopIdFromLocation = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    const queryShopId = urlParams.get('shop_id');
    if (queryShopId) return queryShopId;

    const pathMatch = location.pathname.match(/\/shop\/(\d+)/);
    if (pathMatch) return pathMatch[1];

    const storedShopId = localStorage.getItem('selected_shop_id');
    if (storedShopId) return storedShopId;

    const userInfoStr = localStorage.getItem('userInfo');
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr);
        if (userInfo.shop_id) return String(userInfo.shop_id);
      } catch (e) {
        // ignore a broken userInfo value
      }
    }
    return null;
  }, [location.pathname, location.search]);

  // ---------------------------------------------------------------
  // Currencies that can be chosen = currencies of countries that have a shop
  // ---------------------------------------------------------------
  const fetchAvailableCurrencies = useCallback(async (force = false) => {
    if (!force) {
      try {
        const cached = sessionStorage.getItem(AVAILABLE_CACHE_KEY);
        const cachedAt = sessionStorage.getItem(AVAILABLE_CACHE_TIME_KEY);
        if (cached && cachedAt && Date.now() - parseInt(cachedAt, 10) < AVAILABLE_CACHE_MS) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length) {
            setAvailableList(parsed);
            return;
          }
        }
      } catch (e) {
        // ignore a broken cache
      }
    }

    // 1) the backend knows which countries have shops
    try {
      const response = await apiFetch(
        '/api/currency/settings/available_currencies/',
        {},
        true
      );
      if (response.ok) {
        const data = await response.json();
        const list = normaliseCurrencyList(data.currencies);
        if (list.length) {
          setAvailableList(list);
          sessionStorage.setItem(AVAILABLE_CACHE_KEY, JSON.stringify(list));
          sessionStorage.setItem(AVAILABLE_CACHE_TIME_KEY, Date.now().toString());
          return;
        }
      }
    } catch (err) {
      console.warn('[CurrencyContext] available_currencies failed:', err.message);
    }

    // 2) backup: work it out from the shops' countries
    try {
      const response = await apiFetch('/api/product/shops/', {}, true);
      if (response.ok) {
        const data = await response.json();
        const shops = Array.isArray(data) ? data : data.results || data.shops || [];
        const found = [];
        shops.forEach((shop) => {
          const code = getCurrencyCodeFromCountry(shop.country);
          if (code) {
            found.push({ code, ...describeCurrency(code), countries: [shop.country] });
          }
        });
        const list = normaliseCurrencyList(found);
        if (list.length) {
          setAvailableList(list);
          return;
        }
      }
    } catch (err) {
      console.warn('[CurrencyContext] shops fallback failed:', err.message);
    }

    // 3) nothing worked: the list stays empty -> only the shop's own currency is shown
    // (never the whole world list)
  }, []);

  // The selectable list always contains the shop's own (base) currency.
  const availableCurrencies = useMemo(() => {
    const list = [...availableList];
    if (shopBase.code && !list.some((c) => c.code === shopBase.code)) {
      list.unshift({ ...shopBase, countries: [] });
    }
    return list;
  }, [availableList, shopBase]);

  // ---------------------------------------------------------------
  // Rates (always relative to the shop's BASE currency)
  // ---------------------------------------------------------------
  const loadRatesFor = useCallback(async (baseCode, force = false) => {
    const base = String(baseCode || DEFAULT_BASE.code).toUpperCase();
    try {
      const { rates: freshRates, fetchedAt } = await fetchLatestRates(base, force);
      ratesBaseRef.current = base;
      setRates({ ...freshRates, [base]: 1 });
      setRatesFetchedAt(fetchedAt);
      return { ...freshRates, [base]: 1 };
    } catch (err) {
      console.error('[CurrencyContext] loadRatesFor failed:', err.message);
      setError('Failed to fetch exchange rates');
      if (ratesBaseRef.current !== base) {
        // old rates belong to another base: do not mix them up
        ratesBaseRef.current = base;
        setRates({ [base]: 1 });
        setRatesFetchedAt(0);
      }
      return null;
    }
  }, []);

  // ---------------------------------------------------------------
  // Load everything for one shop
  // ---------------------------------------------------------------
  const resolveBaseFromShopsList = useCallback(async (shopIdParam) => {
    try {
      const response = await apiFetch('/api/product/shops/', {}, true);
      if (!response.ok) return null;
      const data = await response.json();
      const shops = Array.isArray(data) ? data : data.results || data.shops || [];
      const shop = shops.find((s) => String(s.shop_id || s.id) === String(shopIdParam));
      const code = shop ? getCurrencyCodeFromCountry(shop.country) : null;
      return code ? describeCurrency(code) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const loadShopCurrency = useCallback(
    async (shopIdParam, force = false) => {
      const requestId = ++requestIdRef.current;
      const isStale = () => requestId !== requestIdRef.current;

      setLoading(true);
      setError(null);

      let base = DEFAULT_BASE;
      let chosen = DEFAULT_BASE;

      try {
        let data = null;

        if (!force) {
          try {
            const cached = sessionStorage.getItem(settingsCacheKey(shopIdParam));
            const cachedAt = sessionStorage.getItem(settingsCacheTimeKey(shopIdParam));
            if (cached && cachedAt && Date.now() - parseInt(cachedAt, 10) < SETTINGS_CACHE_MS) {
              data = JSON.parse(cached);
            }
          } catch (e) {
            data = null;
          }
        }

        if (!data) {
          const response = await apiFetch(
            `/api/currency/settings/by_shop/?shop_id=${encodeURIComponent(shopIdParam)}`,
            {},
            true
          );
          if (response.ok) {
            data = await response.json();
            sessionStorage.setItem(settingsCacheKey(shopIdParam), JSON.stringify(data));
            sessionStorage.setItem(settingsCacheTimeKey(shopIdParam), Date.now().toString());
          }
        }

        if (data && data.shop_base_currency) {
          // the backend sends the shop's COUNTRY currency as shop_base_*
          const code = String(data.shop_base_currency).toUpperCase();
          base = {
            code,
            symbol: data.shop_base_symbol || getCurrencySymbol(code),
            name: data.shop_base_name || getCurrencyName(code),
          };

          // and the currency the admin picked for display as base_currency
          const chosenCode = String(data.base_currency || code).toUpperCase();
          chosen = {
            code: chosenCode,
            symbol: data.currency_symbol || getCurrencySymbol(chosenCode),
            name: data.currency_name || getCurrencyName(chosenCode),
          };
        } else {
          // backend not reachable: use the shop's country from the shops list
          const derived = await resolveBaseFromShopsList(shopIdParam);
          base = derived || DEFAULT_BASE;
          chosen = base;
        }

        if (isStale()) return;

        const freshRates = await loadRatesFor(base.code, force);
        if (isStale()) return;

        // if no rate exists for the chosen currency, fall back to the base currency
        if (chosen.code !== base.code && !(freshRates && rateFromTable(freshRates, base.code, chosen.code))) {
          console.warn(
            `[CurrencyContext] no rate for ${base.code} -> ${chosen.code}, showing ${base.code}`
          );
          chosen = base;
        }

        setShopBase(base);
        setDisplay(chosen);
      } catch (err) {
        if (isStale()) return;
        console.error('[CurrencyContext] loading shop currency failed:', err);
        setError('Failed to fetch currency settings');
      } finally {
        if (!isStale()) {
          setLoading(false);
          setInitialized(true);
        }
      }
    },
    [loadRatesFor, resolveBaseFromShopsList]
  );

  // ---------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------
  // the list of selectable currencies (once, and again when a shop is added)
  useEffect(() => {
    fetchAvailableCurrencies();
    const handleShopAdded = () => fetchAvailableCurrencies(true);
    window.addEventListener('shopAdded', handleShopAdded);
    return () => window.removeEventListener('shopAdded', handleShopAdded);
  }, [fetchAvailableCurrencies]);

  // load (again) whenever another shop is opened
  const currentShopId = getShopIdFromLocation();
  useEffect(() => {
    if (currentShopId) {
      setShopId(currentShopId);
      localStorage.setItem('selected_shop_id', currentShopId);
      setShopChangeKey((prev) => prev + 1);
      loadShopCurrency(currentShopId, true);
    } else {
      setShopId(null);
      loadRatesFor(DEFAULT_BASE.code, false).finally(() => setInitialized(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId]);

  // refresh the rates every hour
  useEffect(() => {
    const interval = setInterval(() => {
      loadRatesFor(ratesBaseRef.current || shopBase.code || DEFAULT_BASE.code, true);
    }, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [shopBase.code, loadRatesFor]);

  // ---------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------
  /** Choose the currency prices are SHOWN in (must be a currency of a shop country). */
  const updateCurrencySettings = async (newCurrency) => {
    const code = String(newCurrency || '').toUpperCase();

    try {
      setLoading(true);
      setError(null);

      if (!shopId) throw new Error('No shop selected');

      const allowed = availableCurrencies.map((c) => c.code);
      if (!allowed.includes(code)) {
        throw new Error(
          `This currency is not available. Choose one of: ${allowed.join(', ') || shopBase.code}`
        );
      }

      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Not authenticated');

      const response = await apiFetch('/api/currency/settings/update_currency/', {
        method: 'POST',
        body: JSON.stringify({ base_currency: code, shop_id: shopId }),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // make sure we can really convert base -> new currency
      let currentRates = rates;
      if (!rateFromTable(currentRates, shopBase.code, code)) {
        currentRates = (await loadRatesFor(shopBase.code, true)) || currentRates;
      }
      if (!rateFromTable(currentRates, shopBase.code, code)) {
        throw new Error(`No exchange rate available for ${shopBase.code} -> ${code}`);
      }

      setDisplay({
        code,
        symbol: data.currency_symbol || getCurrencySymbol(code),
        name: data.currency_name || getCurrencyName(code),
      });

      sessionStorage.removeItem(settingsCacheKey(shopId));
      sessionStorage.removeItem(settingsCacheTimeKey(shopId));

      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /** Go back to the shop's own (country) currency. */
  const resetToShopCurrency = () => updateCurrencySettings(shopBase.code);

  /** Download fresh exchange rates now. */
  const refreshSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const { rates: freshRates, fetchedAt } = await refreshRatesService(shopBase.code);
      const normalised = { ...freshRates, [shopBase.code]: 1 };
      ratesBaseRef.current = shopBase.code;
      setRates(normalised);
      setRatesFetchedAt(fetchedAt);
      return { success: true, rates: normalised };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => setError(null);

  // ---------------------------------------------------------------
  // Conversion + formatting
  // ---------------------------------------------------------------
  /** 1 unit of fromCode = ? units of toCode (0 when no rate is known). */
  const getRateBetween = useCallback(
    (fromCode, toCode) => rateFromTable(rates, fromCode, toCode),
    [rates]
  );

  const hasRate = useCallback(
    (fromCode, toCode) => rateFromTable(rates, fromCode, toCode) > 0,
    [rates]
  );

  /** Convert a number. If the rate is unknown the number is returned unchanged. */
  const convert = useCallback(
    (amount, fromCode, toCode) => {
      const value = Number(amount || 0);
      const rate = rateFromTable(rates, fromCode, toCode);
      return rate ? value * rate : value;
    },
    [rates]
  );

  const convertAmount = convert;

  /** amount in the BASE currency -> text in the DISPLAY currency, e.g. "£12.50" */
  const formatPrice = useCallback(
    (amount) => {
      const num = Number(amount) || 0;
      const rate = rateFromTable(rates, shopBase.code, display.code);
      if (!rate) return `${shopBase.symbol}${num.toFixed(2)}`;
      return `${display.symbol}${(num * rate).toFixed(2)}`;
    },
    [rates, shopBase, display]
  );

  /** amount in `fromCode` -> text in the DISPLAY currency */
  const formatPriceFromCode = useCallback(
    (amount, fromCode) => {
      const from = String(fromCode || shopBase.code).toUpperCase();
      const num = Number(amount) || 0;
      const rate = rateFromTable(rates, from, display.code);
      if (!rate) return `${getCurrencySymbol(from)}${num.toFixed(2)}`;
      return `${display.symbol}${(num * rate).toFixed(2)}`;
    },
    [rates, shopBase.code, display]
  );

  /** amount in the BASE currency -> text in the BASE currency (no conversion) */
  const formatInShopCurrency = useCallback(
    (amount) => `${shopBase.symbol}${(Number(amount) || 0).toFixed(2)}`,
    [shopBase]
  );

  const getRateFor = useCallback(
    (code) => (code ? Number(rates?.[String(code).toUpperCase()] || 1) : 1),
    [rates]
  );

  // Old names kept so the other pages keep working.
  // getCurrentCurrency() = the currency prices are STORED / ENTERED in (the base).
  const getCurrentCurrency = useCallback(() => shopBase.code, [shopBase.code]);
  const getCurrentCurrencySymbol = useCallback(() => shopBase.symbol, [shopBase.symbol]);
  const getCurrentCurrencyName = useCallback(() => shopBase.name, [shopBase.name]);

  const displayRate = useMemo(
    () => rateFromTable(rates, shopBase.code, display.code),
    [rates, shopBase.code, display.code]
  );

  const shopPkrRate = useMemo(
    () => rateFromTable(rates, shopBase.code, 'PKR') || 1,
    [rates, shopBase.code]
  );

  const pkrRate = useMemo(
    () => rateFromTable(rates, display.code, 'PKR') || 1,
    [rates, display.code]
  );

  const gbpRate = Number(rates?.GBP || 0);
  const exchangeRate = gbpRate > 0 ? 1 / gbpRate : 1;

  const value = {
    // ---- shop BASE currency (country currency) ----
    shopBaseCurrency: shopBase.code,
    shopBaseSymbol: shopBase.symbol,
    shopBaseName: shopBase.name,
    shopCurrencySymbol: shopBase.symbol,

    // ---- DISPLAY currency (chosen on the Currency Settings page) ----
    currency: display.code, // same as displayCurrency (old name)
    displayCurrency: display.code,
    displaySymbol: display.symbol,
    displayName: display.name,
    displayRate, // 1 base = ? display

    // ---- rates ----
    rates,
    ratesFetchedAt,
    lastUpdated: ratesFetchedAt ? new Date(ratesFetchedAt) : null,
    getRateFor,
    getRateBetween,
    hasRate,
    shopPkrRate,
    pkrRate,
    exchangeRate,

    // ---- helpers ----
    getCurrentCurrency,
    getCurrentCurrencySymbol,
    getCurrentCurrencyName,
    convert,
    convertAmount,
    formatPrice,
    formatPriceFromCode,
    formatInShopCurrency,

    // ---- status ----
    loading,
    initialized,
    error,
    shopId,
    shopChangeKey,

    // ---- selectable currencies (only countries that have a shop) ----
    availableCurrencies,

    // ---- actions ----
    updateCurrencySettings,
    resetToShopCurrency,
    refreshSettings,
    clearError,

    // ---- static helpers ----
    getCurrencySymbol,
    getCurrencyName,
    getCurrencyCodeFromCountry,
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export default CurrencyProvider;